'use client';

import { FC, Children, ReactNode, cloneElement, isValidElement } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { MotionSafeVideo } from '@/components/common/MotionSafeVideo';

type EmbedWithHtml = {
  html?: string;
};

type ChipItem = {
  chip_text?: string;
};

type SnapshotPrimary = {
  background_video?: unknown;
  tag?: string | null;
  title?: Content.HeroOverviewBannerSliceDefaultWithChipsAndImagePrimary['title'];
  description?: Content.HeroOverviewBannerSliceDefaultWithChipsAndImagePrimary['description'];
  chips?: ChipItem[];
  featured_video?: Content.HeroOverviewBannerSliceDefaultWithChipsAndImagePrimary['featured_video'];
};

function hasHtmlEmbed(value: unknown): value is EmbedWithHtml {
  return typeof value === 'object' && value !== null && 'html' in value;
}

/**
 * Walks rich-text children and replaces `[highlight]...[/highlight]` and
 * `[highlight_b]...[/highlight_b]` markers in string leaves with colored
 * spans, preserving any nested inline formatting (e.g. <strong>, <em>).
 */
const HIGHLIGHT_REGEX =
  /(\[highlight\][^]*?\[\/highlight\]|\[highlight_b\][^]*?\[\/highlight_b\])/g;

const renderWithBreaks = (text: string): ReactNode => {
  const parts = text.split('[BR]');
  if (parts.length === 1) {
    return text;
  }
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 ? <br /> : null}
    </span>
  ));
};

const renderHighlightString = (text: string): ReactNode => {
  const parts = text.split(HIGHLIGHT_REGEX);
  if (parts.length === 1) {
    return renderWithBreaks(text);
  }
  return parts.map((part, i) => {
    const green = part.match(/^\[highlight\]([^]*?)\[\/highlight\]$/);
    if (green) {
      return (
        <span key={i} className="text-[#05F273]">
          {green[1]}
        </span>
      );
    }
    const blue = part.match(/^\[highlight_b\]([^]*?)\[\/highlight_b\]$/);
    if (blue) {
      return (
        <span key={i} className="text-[#21007c]">
          {blue[1]}
        </span>
      );
    }
    return <span key={i}>{renderWithBreaks(part)}</span>;
  });
};

const withHighlights = (children: ReactNode): ReactNode => {
  return Children.map(children, (child) => {
    if (typeof child === 'string') {
      return renderHighlightString(child);
    }
    if (
      isValidElement<{ children?: ReactNode }>(child) &&
      child.props?.children !== undefined
    ) {
      return cloneElement(child, {
        children: withHighlights(child.props.children),
      });
    }
    return child;
  });
};

/**
 * Props for `VelxSnapshot`.
 */
export type VelxSnapshotProps =
  SliceComponentProps<Content.HeroOverviewBannerSlice>;

/**
 * Component for "VelxSnapshot" Slices.
 */
const VelxSnapshot: FC<VelxSnapshotProps> = ({ slice }) => {
  const {
    background_video,
    tag,
    title,
    description,
    chips = [],
    featured_video,
  } = slice.primary as unknown as SnapshotPrimary;

  // Check if background_video is an embed with HTML
  const videoHtml = hasHtmlEmbed(background_video)
    ? background_video.html
    : null;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-5 overflow-hidden rounded-[24px] bg-[#041118] px-[24px] py-[80px] md:mx-[30px] md:px-[40px] lg:mx-[60px] lg:px-[60px]"
    >
      {/* Background video/image container */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-[24px]">
        <div className="absolute inset-0 bg-[#041118]" />
        {videoHtml ? (
          <div
            className="absolute inset-0 [&>iframe]:h-full [&>iframe]:w-full"
            dangerouslySetInnerHTML={{
              __html: videoHtml.replace(
                /<iframe/,
                '<iframe style="width:100%;height:100%;position:absolute;inset:0;" ',
              ),
            }}
          />
        ) : null}
        <div className="absolute inset-0 bg-[#041118]/70" />
      </div>

      {/* Content container */}
      <div className="relative z-10 mx-auto flex w-full max-w-[1260px] flex-col gap-[40px] px-0 lg:px-[90px]">
        <div className="flex flex-col items-center gap-[24px] text-center">
          {/* Tag/Badge */}
          {tag && (
            <span className="inline-block rounded-full border border-[#00f557] px-[16px] py-[8px] text-[14px] font-normal uppercase leading-[1.2] text-white">
              {tag}
            </span>
          )}

          {/* Title */}
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading1: ({ children }) => (
                  <h2 className="max-w-[1080px] text-[32px] font-semibold capitalize leading-[1.2] text-white lg:text-[42px]">
                    {withHighlights(children)}
                  </h2>
                ),
                heading2: ({ children }) => (
                  <h2 className="max-w-[1080px] text-[32px] font-semibold capitalize leading-[1.2] text-white lg:text-[42px]">
                    {withHighlights(children)}
                  </h2>
                ),
              }}
            />
          )}

          {/* Description */}
          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="max-w-[820px] text-pretty text-[16px] font-light leading-[1.4] text-white">
                    {withHighlights(children)}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
              }}
            />
          )}

          {/* Chips */}
          {chips && chips.length > 0 && (
            <div className="mt-0 flex flex-wrap items-center justify-center gap-[8px]">
              {chips.map((chip, i: number) =>
                chip.chip_text ? (
                  <span
                    key={i}
                    className="rounded-full border border-[#666666] px-[16px] py-[8px] text-[14px] font-normal leading-[1.2] text-[#666666]"
                  >
                    {chip.chip_text}
                  </span>
                ) : null,
              )}
            </div>
          )}
        </div>

        {/* Featured video */}
        {isFilled.linkToMedia(featured_video) && (
          <div className="relative mx-auto h-auto w-full max-w-[1080px] overflow-hidden rounded-[30px] border-[5px] border-[#13094a]">
            <MotionSafeVideo
              className="h-auto w-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={featured_video.url} type="video/mp4" />
              Your browser does not support the video tag.
            </MotionSafeVideo>
          </div>
        )}
      </div>
    </section>
  );
};

export default VelxSnapshot;
